import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

import { Box, Button, Chip, DialogActions, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { Bag, Edit, SearchNormal1 } from 'iconsax-react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

import MainCard from 'components/MainCard';
import CustomTable from 'components/@extended/CustomTable';
import Loader2 from 'components/Loader2';
import Snackbar from 'utils/Snackbar';
import useAuth from 'hooks/useAuth';

import { getAllSubjects } from 'services/subject.service';
import { getAllClassRoom } from 'services/classRoom.service';
import { getAttendanceReport, deleteAttendance } from 'services/attendance.service';
import { getAllBatch } from 'services/batch.service';
import { Tooltip } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import IconButton from 'components/@extended/IconButton';
import { DialogTitle } from '@mui/material';
import { Dialog } from '@mui/material';
import { DialogContent } from '@mui/material';

interface AttendanceRecord {
  _id: string;
  studentId: string;
  firstName: string;
  lastName: string;
  rollNumber?: string;
  email: string;
  classRoomId?: string;
  subjectId?: string;
  batchId?: string;
  present: boolean;
  date: string;
  startTime: string;
  endTime: string;
}

const AttendanceReport = () => {
  const { user, organization } = useAuth();
  const orgId = user?.activeOrganizationId || user?.organizationId?._id;
  const ncvetCompliance = organization?.ncvetCompliance === true;
  const navigate = useNavigate();

  const [classRooms, setClassRooms] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [batchList, setBatchList] = useState<any[]>([]);

  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);

  const [reportData, setReportData] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(1);
  const [size, setSize] = useState(10);

  // Load filters (class+subject or batches for NCVET)
  useEffect(() => {
    if (!orgId) return;
    if (ncvetCompliance) {
      getAllBatch({ orgId, limit: 500, skip: 1, deleted: false })
        .then((res: any) => {
          const list = res?.data?.data?.batchData || [];
          setBatchList(Array.isArray(list) ? list : []);
        })
        .catch(() => Snackbar('Failed to load batches', 'error'));
    } else {
      Promise.all([getAllClassRoom({ organizationId: orgId }), getAllSubjects({ organizationId: orgId })])
        .then(([clsRes, subRes]: any) => {
          setClassRooms(clsRes?.data?.data?.classRoomData || []);
          setSubjects(subRes?.data?.data?.data || []);
        })
        .catch(() => Snackbar('Failed to load filters', 'error'));
    }
  }, [orgId, ncvetCompliance]);

  // Fetch report when all required fields are selected
  useEffect(() => {
    const loadReport = async () => {
      const hasRequired = ncvetCompliance
        ? selectedBatchId && selectedDate && startTime && endTime
        : selectedClass && selectedSubject && selectedDate && startTime && endTime;
      if (!hasRequired) {
        setReportData([]);
        return;
      }

      setLoading(true);
      try {
        const params: any = {
          date: format(selectedDate!, 'yyyy-MM-dd'),
          startTime: format(startTime!, 'HH:mm'),
          endTime: format(endTime!, 'HH:mm')
        };
        if (ncvetCompliance) {
          params.batchId = selectedBatchId;
        } else {
          params.organizationId = orgId;
          params.classRoomId = selectedClass;
          params.subjectId = selectedSubject;
        }
        const res = await getAttendanceReport(params);
        setReportData(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch {
        Snackbar('Failed to load attendance report', 'error');
        setReportData([]);
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [selectedClass, selectedSubject, selectedBatchId, selectedDate, startTime, endTime, orgId, ncvetCompliance]);

  // Search & pagination (client-side)
  const filteredReport = useMemo(() => {
    if (!search.trim()) return reportData;
    const q = search.trim().toLowerCase();
    return reportData.filter((r) => {
      const first = (r.firstName ?? '').trim();
      const last = (r.lastName ?? '').trim();
      const fullName = `${first} ${last}`.trim().toLowerCase();
      const fullNameReversed = `${last} ${first}`.trim().toLowerCase();
      return (
        first.toLowerCase().includes(q) ||
        last.toLowerCase().includes(q) ||
        fullName.includes(q) ||
        fullNameReversed.includes(q) ||
        (r.email ?? '').toLowerCase().includes(q) ||
        (String(r.rollNumber ?? '')).toLowerCase().includes(q)
      );
    });
  }, [reportData, search]);

  const paginatedReport = useMemo(() => {
    const start = (pageIndex - 1) * size;
    return filteredReport.slice(start, start + size);
  }, [filteredReport, pageIndex, size]);

  useEffect(() => {
    setPageIndex(1);
  }, [search, reportData.length]);

  // Counts (from filtered report when searching, else full report)
  const presentCount = useMemo(() => filteredReport.filter((r) => r.present).length, [filteredReport]);
  const absentCount = useMemo(() => filteredReport.filter((r) => !r.present).length, [filteredReport]);

  // Navigate to edit screen (pass batchId for NCVET so AttendanceCheck opens batch-wise)
  const handleEditAttendance = (record: AttendanceRecord) => {
    if (ncvetCompliance && record.batchId) {
      navigate('/attendance', {
        state: {
          batchId: record.batchId,
          date: record.date,
          startTime: record.startTime,
          endTime: record.endTime
        }
      });
    } else {
      navigate('/attendance', {
        state: {
          classRoomId: record.classRoomId,
          subjectId: record.subjectId,
          date: record.date,
          startTime: record.startTime,
          endTime: record.endTime
        }
      });
    }
  };

  const handleOpenDialogBox = (id: string) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteId(null);
  };
  const handleConfirmDelete = async () => {
    if (!deleteId) return;

    try {
      await deleteAttendance(deleteId);
      Snackbar('Attendance deleted successfully', 'success');

      setReportData((prev) => prev.filter((row) => row._id !== deleteId));
    } catch {
      Snackbar('Failed to delete attendance', 'error');
    } finally {
      handleCloseDialog();
    }
  };

  const columns = useMemo(
    () => [
      { Header: 'Roll No', accessor: 'studentNumber' },
      {
        Header: 'Student Name',
        Cell: ({ row }: any) => (
          <Typography>
            {row.original.firstName} {row.original.lastName}
          </Typography>
        )
      },
      { Header: 'Email', accessor: 'email' },
      {
        Header: 'Status',
        Cell: ({ row }: any) =>
          row.original.present ? <Chip label="Present" color="success" size="small" /> : <Chip label="Absent" color="error" size="small" />
      },
      {
        Header: 'Session',
        Cell: ({ row }: any) => (
          <Typography>
            {format(new Date(row.original.date), 'dd MMM yyyy')} | {row.original.startTime} - {row.original.endTime}
          </Typography>
        )
      },
      {
        Header: 'Actions',
        Cell: ({ row }: any) => (
          <Stack direction="row" spacing={0}>
            {/* Edit */}
            <Tooltip title={<FormattedMessage id="Edit" />}>
              <span>
                <IconButton color="primary" onClick={() => handleEditAttendance(row.original)}>
                  <Edit size="20" />
                </IconButton>
              </span>
            </Tooltip>

            {/* Delete */}
            <Tooltip title={<FormattedMessage id="Delete" />}>
              <span>
                <IconButton color="error" onClick={() => handleOpenDialogBox(row.original._id)}>
                  <Bag size="20" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        )
      }
    ],
    []
  );

  return (
    <MainCard
      title="Attendance Report"
      secondary={
        reportData.length > 0 ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search"
              size="small"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPageIndex(1);
                  setSearch(searchInput);
                }
              }}
            />
            <IconButton
              variant="outlined"
              onClick={() => {
                setPageIndex(1);
                setSearch(searchInput);
              }}
            >
              <SearchNormal1 />
            </IconButton>
          </Stack>
        ) : null
      }
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3} flexWrap="wrap">
        {ncvetCompliance ? (
          <TextField
            select
            label="Batch"
            value={selectedBatchId}
            onChange={(e) => setSelectedBatchId(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">Select Batch</MenuItem>
            {batchList.map((b) => {
              const label = [b.name,''].filter(Boolean).join(' ') || b._id;
              return (
                <MenuItem key={b._id} value={b._id}>
                  {label}
                </MenuItem>
              );
            })}
          </TextField>
        ) : (
          <>
            <TextField select label="Class" value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} sx={{ minWidth: 200 }}>
              <MenuItem value="">Select Class</MenuItem>
              {classRooms.map((cls) => (
                <MenuItem key={cls._id} value={cls._id}>
                  {cls.className} ({cls.divisionName})
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Subject"
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Select Subject</MenuItem>
              {subjects.map((sub) => (
                <MenuItem key={sub._id} value={sub._id}>
                  {sub.subjectName}
                </MenuItem>
              ))}
            </TextField>
          </>
        )}

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <MobileDatePicker label="Date" value={selectedDate} onChange={(date) => setSelectedDate(date)} format="dd/MM/yyyy" />

          <TimePicker label="Start Time" value={startTime} onChange={(time) => setStartTime(time)} />

          <TimePicker label="End Time" value={endTime} onChange={(time) => setEndTime(time)} />
        </LocalizationProvider>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" minHeight="40vh">
          <Loader2 />
        </Box>
      ) : reportData.length === 0 ? (
        <Box display="flex" justifyContent="center" minHeight="40vh" alignItems="center">
          <Typography>
            {ncvetCompliance && !selectedBatchId
              ? 'Select batch, date and time to view report'
              : 'No attendance records found'}
          </Typography>
        </Box>
      ) : (
        <>
          <Stack direction="row" spacing={2} mb={2}>
            <Chip label={`Present: ${presentCount}`} color="success" variant="outlined" />
            <Chip label={`Absent: ${absentCount}`} color="error" variant="outlined" />
            <Chip label={`Total: ${presentCount + absentCount}`} variant="outlined" />
          </Stack>

          <CustomTable
            columns={columns}
            data={paginatedReport}
            sortArray={[]}
            sort={[]}
            setSortingData={() => {}}
            search={search}
            notFound={false}
            count={filteredReport.length}
            size={size}
            pageIndex={pageIndex}
            handlePageChange={(_e: any, page: number) => setPageIndex(page)}
            handleSizeChange={(e: SelectChangeEvent<number>) => {
              setSize(Number(e.target.value));
              setPageIndex(1);
            }}
          />
        </>
      )}
      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDialog}>
        <DialogTitle>
          <FormattedMessage id="Delete Attendance" />
        </DialogTitle>
        <DialogContent>
          <Typography>
            <FormattedMessage id="Are you sure you want to delete this attendance record?" />
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            <FormattedMessage id="Cancel" />
          </Button>
          <Button color="error" onClick={handleConfirmDelete}>
            <FormattedMessage id="Delete" />
          </Button>
        </DialogActions>
      </Dialog>
    </MainCard>
  );
};

export default AttendanceReport;
